import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { InlineConversationBrief } from "@/hooks/useInlineConversation";
import type { FloatingCelebrationContext } from "@/lib/floatingCelebrationCheckout";
import type { DashboardClientActionHandlers } from "@/lib/dashboardClientActions";
import type { TeamClientActionHandlers } from "@/lib/teamClientActions";
import { buildInitialBriefFromSearchParams } from "@/lib/chatBriefFromParams";

/** Compact summary shown in floating concierge when adjusting from ApprovalModal. */
export type FloatingAdjustRecommendationSummary = {
  productSummary: string;
  bakery: string;
  totalCents: number;
};

type FloatingConciergeUiContextValue = {
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  panelExpanded: boolean;
  setPanelExpanded: (expanded: boolean) => void;
  togglePanelExpanded: () => void;
  isSuppressed: boolean;
  registerSuppression: (id: string, active: boolean) => void;
  celebrationContext: FloatingCelebrationContext | null;
  setCelebrationContext: (ctx: FloatingCelebrationContext | null) => void;
  adjustRecommendationSummary: FloatingAdjustRecommendationSummary | null;
  setAdjustRecommendationSummary: (
    summary: FloatingAdjustRecommendationSummary | null,
  ) => void;
  /** Brief from a deep link (?brief=…) — remounts chat when raw_text is present. */
  deepLinkInitialBrief: InlineConversationBrief | null;
  /** Bumps when a deep link carries raw_text so InlineConcierge auto-sends. */
  conciergeMountGeneration: number;
  openWithCelebrationContext: (ctx: FloatingCelebrationContext) => void;
  openForAdjust: (
    ctx: FloatingCelebrationContext,
    summary?: FloatingAdjustRecommendationSummary | null,
  ) => void;
  /** Open floating concierge from /chat-style URL params (authed surfaces). */
  applyConciergeDeepLink: (params: URLSearchParams) => void;
  dashboardClientActions: DashboardClientActionHandlers | null;
  registerDashboardClientActions: (
    handlers: DashboardClientActionHandlers | null,
  ) => void;
  teamClientActions: TeamClientActionHandlers | null;
  registerTeamClientActions: (
    handlers: TeamClientActionHandlers | null,
  ) => void;
};

const FloatingConciergeUiContext =
  createContext<FloatingConciergeUiContextValue | null>(null);

export function FloatingConciergeProvider({ children }: { children: ReactNode }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [suppressions, setSuppressions] = useState<Set<string>>(() => new Set());
  const [celebrationContext, setCelebrationContext] =
    useState<FloatingCelebrationContext | null>(null);
  const [adjustRecommendationSummary, setAdjustRecommendationSummary] =
    useState<FloatingAdjustRecommendationSummary | null>(null);
  const [deepLinkInitialBrief, setDeepLinkInitialBrief] =
    useState<InlineConversationBrief | null>(null);
  const [conciergeMountGeneration, setConciergeMountGeneration] = useState(0);
  const [dashboardClientActions, setDashboardClientActions] =
    useState<DashboardClientActionHandlers | null>(null);
  const [teamClientActions, setTeamClientActions] =
    useState<TeamClientActionHandlers | null>(null);

  const registerDashboardClientActions = useCallback(
    (handlers: DashboardClientActionHandlers | null) => {
      setDashboardClientActions(handlers);
    },
    [],
  );

  const registerTeamClientActions = useCallback(
    (handlers: TeamClientActionHandlers | null) => {
      setTeamClientActions(handlers);
    },
    [],
  );

  const registerSuppression = useCallback((id: string, active: boolean) => {
    setSuppressions((prev) => {
      const next = new Set(prev);
      if (active) next.add(id);
      else next.delete(id);
      if (next.size === prev.size && [...next].every((k) => prev.has(k))) {
        return prev;
      }
      return next;
    });
  }, []);

  const isSuppressed = suppressions.size > 0;

  const togglePanelExpanded = useCallback(() => {
    setPanelExpanded((v) => !v);
  }, []);

  const openWithCelebrationContext = useCallback((ctx: FloatingCelebrationContext) => {
    setCelebrationContext(ctx);
    setPanelOpen(true);
    setPanelExpanded(true);
  }, []);

  const openForAdjust = useCallback(
    (
      ctx: FloatingCelebrationContext,
      summary: FloatingAdjustRecommendationSummary | null = null,
    ) => {
      setCelebrationContext(ctx);
      setAdjustRecommendationSummary(summary);
      setPanelOpen(true);
      setPanelExpanded(true);
    },
    [],
  );

  const applyConciergeDeepLink = useCallback((params: URLSearchParams) => {
    const brief = buildInitialBriefFromSearchParams(params);
    setAdjustRecommendationSummary(null);

    if (brief?.recipient_id && brief.recipient_name) {
      setCelebrationContext({
        recipientId: brief.recipient_id,
        recipientName: brief.recipient_name,
        occasion: brief.occasion,
        celebrationId: params.get("celebration_id") ?? undefined,
        celebrationDate: params.get("celebration_date") ?? undefined,
      });
    } else if (!brief?.raw_text) {
      setCelebrationContext(null);
    }

    if (brief?.raw_text) {
      setDeepLinkInitialBrief(brief);
      setConciergeMountGeneration((g) => g + 1);
    } else {
      setDeepLinkInitialBrief(null);
    }

    setPanelOpen(true);
    setPanelExpanded(true);
  }, []);

  const value = useMemo(
    () => ({
      panelOpen,
      setPanelOpen,
      panelExpanded,
      setPanelExpanded,
      togglePanelExpanded,
      isSuppressed,
      registerSuppression,
      celebrationContext,
      setCelebrationContext,
      adjustRecommendationSummary,
      setAdjustRecommendationSummary,
      deepLinkInitialBrief,
      conciergeMountGeneration,
      openWithCelebrationContext,
      openForAdjust,
      applyConciergeDeepLink,
      dashboardClientActions,
      registerDashboardClientActions,
      teamClientActions,
      registerTeamClientActions,
    }),
    [
      panelOpen,
      panelExpanded,
      togglePanelExpanded,
      isSuppressed,
      registerSuppression,
      celebrationContext,
      adjustRecommendationSummary,
      deepLinkInitialBrief,
      conciergeMountGeneration,
      openWithCelebrationContext,
      openForAdjust,
      applyConciergeDeepLink,
      dashboardClientActions,
      registerDashboardClientActions,
      teamClientActions,
      registerTeamClientActions,
    ],
  );

  return (
    <FloatingConciergeUiContext.Provider value={value}>
      {children}
    </FloatingConciergeUiContext.Provider>
  );
}

export function useFloatingConciergeUi(): FloatingConciergeUiContextValue {
  const ctx = useContext(FloatingConciergeUiContext);
  if (!ctx) {
    throw new Error("useFloatingConciergeUi must be used within FloatingConciergeProvider");
  }
  return ctx;
}

/** Hide the floating launcher/panel while inline drawer or checkout owns the concierge surface. */
export function useRegisterFloatingConciergeSuppression(id: string, active: boolean) {
  const ctx = useContext(FloatingConciergeUiContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.registerSuppression(id, active);
    return () => ctx.registerSuppression(id, false);
  }, [id, active, ctx]);
}
